import React from 'react';
import { useRelayEnvironment } from 'react-relay';
import { useRegisterNudgeTarget } from '@atlassiansox/nudge-tooltip';
import { Box, Grid, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';
import {
	JIRA_PROJECT_TYPE_CORE_PROJECT,
	type JiraProjectType,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { UNSAFE_noExposureExp, expVal } from '@atlassian/jira-feature-experiments';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { JWM_SUMMARY_VIEW_NUDGE_ID } from '@atlassian/jira-onboarding-core/src/constants.tsx';
import { SummaryViewNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/jwm-summary-view/async.tsx';
import {
	useOverviewCountWithRouteResourceData,
	usePriorityBreakdownWithRouteResourceData,
	useStatusOverviewWithRouteResourceData,
	useTypesOfWorkWithRouteResourceAndPaginatedData,
	useTeamWorkloadWithRouteResourceAndPaginatedData,
	useRecentActivityWithRouteResourceAndPaginatedData,
	useEpicProgressOrRelatedProjectsWithData,
	useFastRecentActivityWithData,
	useOverviewCountWithData,
	usePriorityBreakdownWithData,
	useRecentActivityWithPaginatedData,
	useStatusOverviewWithData,
	useTeamWorkloadWithPaginatedData,
	useTypesOfWorkWithPaginatedData,
} from '../gadgets/index.tsx';
import { SUMMARY_PAGE_DATA_QUERY } from '../../services/summary-data/gql.tsx';
import { UFOCustomHoverData } from '../../common/ui/ufo-custom-data-toggle/index.tsx';

type ProjectSummaryProps = {
	projectId: string;
	projectKey: string;
	projectType: JiraProjectType;
	// TODO clean up or refactor as a part of HL-1930
	useRouteResource?: boolean;
};

export const ProjectSummary = ({
	projectId,
	projectKey,
	projectType,
	useRouteResource,
}: ProjectSummaryProps) => {
	const cloudId = useCloudId();
	const accountId = useAccountId() || null;
	const relayEnvironment = useRelayEnvironment();
	const isBusinessProject = projectType === JIRA_PROJECT_TYPE_CORE_PROJECT;
	const epicProgressOrRelatedProjectsBoundaryId =
		projectType === JIRA_PROJECT_TYPE_CORE_PROJECT
			? 'jira-summary-related-projects'
			: 'jira-summary-epic-progress';

	// TODO clean up or refactor WithRouteResourceData components as a part of HL-1930
	const OverviewDoneCountWithRouteResourceData = useOverviewCountWithRouteResourceData('done');
	const OverviewUpdatedCountWithRouteResourceData =
		useOverviewCountWithRouteResourceData('updated');
	const OverviewCreatedCountWithRouteResourceData =
		useOverviewCountWithRouteResourceData('created');
	const OverviewDueCountWithRouteResourceData = useOverviewCountWithRouteResourceData('due');
	const StatusOverviewWithRouteResourceData = useStatusOverviewWithRouteResourceData();
	const PriorityBreakdownWithRouteResourceData = usePriorityBreakdownWithRouteResourceData();
	const TypesOfWorkWithRouteResourceAndPaginatedData =
		useTypesOfWorkWithRouteResourceAndPaginatedData(cloudId, projectKey, SUMMARY_PAGE_DATA_QUERY);
	const TeamWorkloadWithRouteResourceAndPaginatedData =
		useTeamWorkloadWithRouteResourceAndPaginatedData(cloudId, projectKey, SUMMARY_PAGE_DATA_QUERY);
	const RecentActivityWithRouteResourceAndPaginatedData =
		useRecentActivityWithRouteResourceAndPaginatedData(projectKey);

	// components which don't utilise router resources
	const OverviewDoneCountWithData = useOverviewCountWithData(
		cloudId,
		projectKey,
		'done',
		SUMMARY_PAGE_DATA_QUERY,
	);

	const OverviewUpdatedCountWithData = useOverviewCountWithData(
		cloudId,
		projectKey,
		'updated',
		SUMMARY_PAGE_DATA_QUERY,
	);

	const OverviewCreatedCountWithData = useOverviewCountWithData(
		cloudId,
		projectKey,
		'created',
		SUMMARY_PAGE_DATA_QUERY,
	);

	const OverviewDueCountWithData = useOverviewCountWithData(
		cloudId,
		projectKey,
		'due',
		SUMMARY_PAGE_DATA_QUERY,
	);

	const StatusOverviewWithData = useStatusOverviewWithData(
		cloudId,
		projectKey,
		SUMMARY_PAGE_DATA_QUERY,
	);

	const PriorityBreakdownWithData = usePriorityBreakdownWithData(
		cloudId,
		projectKey,
		SUMMARY_PAGE_DATA_QUERY,
	);

	const RecentActivityWithPaginatedData = useRecentActivityWithPaginatedData(projectKey);

	const FastRecentActivityWithData = useFastRecentActivityWithData(projectId);

	const TypesOfWorkWithPaginatedData = useTypesOfWorkWithPaginatedData(
		cloudId,
		projectKey,
		SUMMARY_PAGE_DATA_QUERY,
	);

	const TeamWorkloadWithPaginatedData = useTeamWorkloadWithPaginatedData(
		cloudId,
		projectKey,
		SUMMARY_PAGE_DATA_QUERY,
	);

	const EpicProgressOrRelatedProjectsWithData = useEpicProgressOrRelatedProjectsWithData(
		relayEnvironment,
		cloudId,
		projectKey,
		projectId,
		projectType,
		accountId,
		epicProgressOrRelatedProjectsBoundaryId,
	);

	const fastActivityFeedEnabled =
		!isBusinessProject && expVal('helios_fast_activity_feed', 'isEnabled', false);

	const [config] = UNSAFE_noExposureExp('tailored_onboarding_m3');
	const isTailoredOnboardingVariant = config.get('isEnabled', false);
	const { ref: summaryViewNudgeRef } = useRegisterNudgeTarget<HTMLDivElement>(
		JWM_SUMMARY_VIEW_NUDGE_ID,
		true,
	);

	return (
		<>
			<Grid xcss={responsiveStyles} gap="space.300" alignItems="center">
				<Box xcss={[baseStyle, xsBoxStyles, overviewCountDoneStyle]}>
					{useRouteResource ? (
						<OverviewDoneCountWithRouteResourceData />
					) : (
						<OverviewDoneCountWithData />
					)}
				</Box>
				<Box xcss={[baseStyle, xsBoxStyles, overviewCountUpdatedStyle]}>
					{useRouteResource ? (
						<OverviewUpdatedCountWithRouteResourceData />
					) : (
						<OverviewUpdatedCountWithData />
					)}
				</Box>
				<Box xcss={[baseStyle, xsBoxStyles, overviewCountCreatedStyle]}>
					{useRouteResource ? (
						<OverviewCreatedCountWithRouteResourceData />
					) : (
						<OverviewCreatedCountWithData />
					)}
				</Box>
				<Box xcss={[baseStyle, xsBoxStyles, overviewCountDueStyle]}>
					{useRouteResource ? (
						<OverviewDueCountWithRouteResourceData />
					) : (
						<OverviewDueCountWithData />
					)}
				</Box>
				<Box
					xcss={[baseStyle, mdBoxStyles, statusOverviewStyle]}
					ref={isTailoredOnboardingVariant ? summaryViewNudgeRef : undefined}
				>
					{useRouteResource ? <StatusOverviewWithRouteResourceData /> : <StatusOverviewWithData />}
				</Box>
				{isTailoredOnboardingVariant && <SummaryViewNudgeAsync />}
				<Box xcss={[baseStyle, mdBoxStyles, recentActivityStyle]}>
					{/* eslint-disable-next-line no-nested-ternary */}
					{fastActivityFeedEnabled ? (
						<FastRecentActivityWithData />
					) : useRouteResource ? (
						<RecentActivityWithRouteResourceAndPaginatedData />
					) : (
						<RecentActivityWithPaginatedData />
					)}
				</Box>
				<Box xcss={[baseStyle, mdBoxStyles, priorityBreakdownStyle]}>
					{useRouteResource ? (
						<PriorityBreakdownWithRouteResourceData />
					) : (
						<PriorityBreakdownWithData />
					)}
				</Box>
				<Box xcss={[baseStyle, mdBoxStyles, typesOfWorkStyle]}>
					{useRouteResource ? (
						<TypesOfWorkWithRouteResourceAndPaginatedData />
					) : (
						<TypesOfWorkWithPaginatedData />
					)}
				</Box>
				<Box xcss={[baseStyle, mdBoxStyles, teamWorkloadStyle]}>
					{useRouteResource ? (
						<TeamWorkloadWithRouteResourceAndPaginatedData />
					) : (
						<TeamWorkloadWithPaginatedData />
					)}
				</Box>
				<Box xcss={[baseStyle, mdBoxStyles, epicProgressStyle]}>
					<EpicProgressOrRelatedProjectsWithData />
				</Box>
			</Grid>
			<UFOCustomHoverData hasHoverEvent={false} />
		</>
	);
};

const xsBoxStyles = xcss({
	height: '64px',
	padding: 'space.050',
});
// const smHeight = xcss({ height: '260px' });
const mdBoxStyles = xcss({
	height: '320px',
	padding: 'space.200',
});
// const lgHeight = xcss({ height: '400px' });
const baseStyle = xcss({
	borderRadius: 'border.radius.200',
	outlineWidth: 'border.width',
	outlineStyle: 'solid',
	outlineColor: 'color.border',
	width: '100%',
	backgroundColor: 'elevation.surface.overlay',
});
const overviewCountDoneStyle = xcss({ gridArea: 'overviewCountDone' });
const overviewCountUpdatedStyle = xcss({ gridArea: 'overviewCountUpdated' });
const overviewCountCreatedStyle = xcss({ gridArea: 'overviewCountCreated' });
const overviewCountDueStyle = xcss({ gridArea: 'overviewCountDue' });
const statusOverviewStyle = xcss({ gridArea: 'statusOverview' });
const recentActivityStyle = xcss({ gridArea: 'recentActivity' });
const priorityBreakdownStyle = xcss({ gridArea: 'priorityBreakdown' });
const typesOfWorkStyle = xcss({ gridArea: 'typesOfWork' });
const teamWorkloadStyle = xcss({ gridArea: 'teamWorkload' });
const epicProgressStyle = xcss({ gridArea: 'epicProgress' });

const responsiveStyles = xcss({
	[media.above.xxs]: {
		gridTemplateAreas:
			"'overviewCountDone'\n'overviewCountUpdated'\n'overviewCountCreated'\n'overviewCountDue'\n'statusOverview'\n'recentActivity'\n'priorityBreakdown'\n'typesOfWork'\n'teamWorkload'\n'epicProgress'",
		gridTemplateColumns: '1fr',
	},
	[media.above.sm]: {
		gridTemplateAreas:
			"'overviewCountDone overviewCountUpdated overviewCountCreated overviewCountDue'\n'statusOverview statusOverview recentActivity recentActivity'\n'priorityBreakdown priorityBreakdown typesOfWork typesOfWork'\n'teamWorkload teamWorkload epicProgress epicProgress'",
		gridTemplateColumns: 'repeat(4, 1fr)',
	},
	width: '100%',
});
